import React, { useEffect, useMemo, useState } from "react";
import "./_academiaSchedule.scss";

/**
 * AcademiaSchedule
 * ----------------------------------------------------------------------------
 * Calendar-style timetable preview matching the "Academia Schedule" mock.
 *
 * This is a presentational shell powered by mock data so the design can be
 * reviewed in-app. Real data will be wired later via these props:
 *
 *   props.role            -> "student" | "teacher"   (default: "student")
 *   props.personName      -> string                  (header subtitle)
 *   props.daySchedule     -> [{ id, periodNo, start, end, subject, code,
 *                              teacherName, teacherInitials, room,
 *                              status: "done"|"now"|"upcoming"|"break" }]
 *   props.monthMatrix     -> [[ { day, inMonth, periods, dotColor } ]]   (6x7)
 *   props.upcoming        -> [{ date, month, title, subtitle, inDays }]
 *
 * When props are omitted, deterministic mock data is rendered (matches the
 * screenshot the design team shared).
 * ----------------------------------------------------------------------------
 */

const WEEK_LABELS = ["M", "T", "W", "T", "F", "S", "S"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const MOCK_DAY = [
    { id: 1, periodNo: "P1", start: "08:30", end: "09:25", duration: "55 min", subject: "Linear Algebra",       code: "MA-201", teacherName: "Prof. N. Iyer",      teacherInitials: "NI", room: "B-204", status: "done" },
    { id: 2, periodNo: "P2", start: "09:30", end: "10:25", duration: "55 min", subject: "Data Structures",      code: "CS-202", teacherName: "Dr. K. Rao",         teacherInitials: "KR", room: "Lab-3", status: "done" },
    { id: 3, periodNo: "",   start: "10:30", end: "10:45", duration: "",       subject: "Short Break",                                                                                                  status: "break" },
    { id: 4, periodNo: "P3", start: "10:45", end: "11:40", duration: "55 min", subject: "Discrete Mathematics", code: "MA-203", teacherName: "Prof. S. Mehta",     teacherInitials: "SM", room: "B-110", status: "done" },
    { id: 5, periodNo: "P4", start: "11:45", end: "12:40", duration: "55 min", subject: "Technical Writing",    code: "EN-101", teacherName: "Ms. A. Fernandes",   teacherInitials: "AF", room: "A-301", status: "done" },
    { id: 6, periodNo: "",   start: "12:40", end: "13:30", duration: "",       subject: "Lunch Break",                                                                                                  status: "live-break" },
    { id: 7, periodNo: "P5", start: "13:30", end: "14:25", duration: "55 min", subject: "Operating Systems",    code: "CS-204", teacherName: "Dr. R. Pillai",      teacherInitials: "RP", room: "Lab-1", status: "upcoming" },
];

const MOCK_UPCOMING = [
    { day: "13", month: "MAY", title: "Mid-Term Exams",   subtitle: "Begins · Block A & B", inDays: "2d" },
    { day: "14", month: "MAY", title: "Mid-Term · Day 2", subtitle: "All departments",      inDays: "3d" },
    { day: "15", month: "MAY", title: "Mid-Term · Day 3", subtitle: "All departments",      inDays: "4d" },
    { day: "22", month: "MAY", title: "Buddha Purnima",   subtitle: "Holiday",              inDays: "11d" },
];

// Build a month matrix (6 weeks x 7 days, Monday-first) with random-ish but
// deterministic period counts so the preview looks alive.
function buildMockMonthMatrix(year, month, todayDay) {
    const first = new Date(year, month, 1);
    // JS getDay(): Sun=0..Sat=6 -> convert to Mon=0..Sun=6
    const firstWeekday = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const prevMonthDays = new Date(year, month, 0).getDate();

    const cells = [];
    // leading days
    for (let i = firstWeekday - 1; i >= 0; i--) {
        cells.push({ day: prevMonthDays - i, inMonth: false });
    }
    // in-month days
    for (let d = 1; d <= daysInMonth; d++) {
        const weekday = new Date(year, month, d).getDay(); // 0=Sun..6=Sat
        const isSunday = weekday === 0;
        const periods = isSunday ? 0 : 4 + ((d * 7) % 3); // 4..6 periods
        cells.push({
            day: d,
            inMonth: true,
            isToday: d === todayDay,
            periods,
            holiday: isSunday,
            hasExam: [13, 14, 15].includes(d),
            hasEvent: d === 22,
        });
    }
    // trailing days
    while (cells.length % 7 !== 0 || cells.length < 42) {
        cells.push({ day: cells.length - (firstWeekday + daysInMonth) + 1, inMonth: false });
        if (cells.length >= 42) break;
    }
    // chunk into weeks
    const matrix = [];
    for (let i = 0; i < cells.length; i += 7) matrix.push(cells.slice(i, i + 7));
    return matrix;
}

function useClock() {
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000 * 30);
        return () => clearInterval(id);
    }, []);
    return now;
}

export default function AcademiaSchedule(props) {
    const {
        role: roleProp = "student",
        personName,
        daySchedule = MOCK_DAY,
        upcoming = MOCK_UPCOMING,
        monthMatrix: monthMatrixProp,
    } = props;

    const [role, setRole] = useState(roleProp);
    useEffect(() => {
        setRole(roleProp);
    }, [roleProp]);

    const handleRoleChange = (newRole) => {
        setRole(newRole);
        if (props.onRoleChange) {
            props.onRoleChange(newRole);
        }
    };

    const now = useClock();

    // Header date (e.g. "Monday · 11 May 2026")
    const dateLine = useMemo(() => {
        const d = now;
        const wk = d.toLocaleDateString(undefined, { weekday: "long" });
        const day = d.getDate();
        const mon = d.toLocaleDateString(undefined, { month: "long" });
        const yr = d.getFullYear();
        return `${wk} · ${day} ${mon} ${yr}`;
    }, [now]);

    const monthLabel = `${MONTHS[now.getMonth()]} ${now.getFullYear()}`;
    const monthMatrix = useMemo(() => {
        if (monthMatrixProp && monthMatrixProp.length > 0) return monthMatrixProp;
        return buildMockMonthMatrix(now.getFullYear(), now.getMonth(), now.getDate());
    }, [now, monthMatrixProp]);

    // Aggregate stats for the day card
    const periodsCount = daySchedule.filter((p) => p.status !== "break" && p.status !== "live-break" && p.periodNo).length;
    const locationsCount = new Set(daySchedule.filter((p) => p.room).map((p) => p.room)).size;
    const totalMinutes = daySchedule
        .filter((p) => p.duration)
        .reduce((acc, p) => acc + parseInt(p.duration, 10), 0);
    const classTime = `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`;

    // "Live" period — first item with status === "live-break" or "now"
    const livePeriod = daySchedule.find((p) => p.status === "live-break" || p.status === "now");
    const liveProgress = 18; // mock %; replace with computed time progress

    const liveTime = now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });

    return (
        <div className="academia-schedule">
            {/* ── Header ───────────────────────────────────────────────── */}
            <div className="as-topbar">
                <div className="as-brand">
                    <span className="as-brand-mark">A</span>
                    <span className="as-brand-text">
                        <b>Academia</b> Schedule
                    </span>
                </div>
                <div className="as-topbar-right">
                    <div className="as-role-toggle">
                        <button
                            type="button"
                            className={role === "student" ? "active" : ""}
                            onClick={() => handleRoleChange("student")}
                        >
                            Student
                        </button>
                        <button
                            type="button"
                            className={role === "teacher" ? "active" : ""}
                            onClick={() => handleRoleChange("teacher")}
                        >
                            Teacher
                        </button>
                    </div>
                    <span className="as-clock">
                        <span className="as-clock-dot" /> {liveTime}
                    </span>
                </div>
            </div>

            {/* ── Body grid ─────────────────────────────────────────────── */}
            <div className="as-body">
                {/* LEFT: Today + period list */}
                <div className="as-main">
                    <div className="as-today-card">
                        <div className="as-today-header">
                            <div>
                                <div className="as-today-title">
                                    Today <span className="as-pill as-pill--green">• Working day</span>
                                </div>
                                <div className="as-today-sub">{dateLine}{personName ? ` · ${personName}` : ""}</div>
                            </div>
                            <div className="as-stats">
                                <div className="as-stat">
                                    <div className="as-stat-num">{periodsCount}</div>
                                    <div className="as-stat-lbl">Periods</div>
                                </div>
                                <div className="as-stat">
                                    <div className="as-stat-num">{classTime}</div>
                                    <div className="as-stat-lbl">Class time</div>
                                </div>
                                <div className="as-stat">
                                    <div className="as-stat-num">{locationsCount}</div>
                                    <div className="as-stat-lbl">Locations</div>
                                </div>
                            </div>
                        </div>

                        {/* Live-now strip */}
                        {livePeriod && (
                            <div className="as-live">
                                <div className="as-live-icon">
                                    <i className="fas fa-clock" />
                                </div>
                                <div className="as-live-body">
                                    <div className="as-live-tag">• LIVE NOW</div>
                                    <div className="as-live-title">{livePeriod.subject}</div>
                                    <div className="as-live-sub">
                                        {livePeriod.code || "—"} · {livePeriod.teacherName || "—"} · {livePeriod.room || "—"}
                                    </div>
                                    <div className="as-live-bar">
                                        <div className="as-live-bar-fill" style={{ width: `${liveProgress}%` }} />
                                    </div>
                                </div>
                                <div className="as-live-right">
                                    <div className="as-live-end">Ends {livePeriod.end}</div>
                                    <div className="as-live-pct">{liveProgress}% complete</div>
                                </div>
                            </div>
                        )}

                        {/* Period list */}
                        <ul className="as-periods">
                            {daySchedule.map((p) => {
                                if (p.status === "break" || p.status === "live-break") {
                                    return (
                                        <li key={p.id} className={`as-period as-period--break ${p.status === "live-break" ? "is-live" : ""}`}>
                                            <div className="as-period-time">
                                                <div className="as-period-time-start">{p.start}</div>
                                                <div className="as-period-time-sep">-</div>
                                                <div className="as-period-time-end">{p.end}</div>
                                            </div>
                                            <div className="as-period-break-label">{p.subject}</div>
                                        </li>
                                    );
                                }
                                return (
                                    <li key={p.id} className={`as-period is-${p.status}`}>
                                        <div className="as-period-time">
                                            <div className="as-period-time-start">{p.start}</div>
                                            <div className="as-period-time-arrow">↓ {p.end}</div>
                                            <div className="as-period-time-dur">{p.duration}</div>
                                        </div>
                                        <div className="as-period-avatar" title={p.teacherName}>
                                            {p.teacherInitials}
                                        </div>
                                        <div className="as-period-body">
                                            <div className="as-period-title">
                                                {p.periodNo && <span className="as-period-no">{p.periodNo}</span>}
                                                <span>{p.subject}</span>
                                            </div>
                                            <div className="as-period-meta">
                                                {p.code} · {p.teacherName}
                                            </div>
                                        </div>
                                        <div className="as-period-right">
                                            {p.status === "done" && <div className="as-period-status">DONE</div>}
                                            {p.status === "now" && <div className="as-period-status as-period-status--now">NOW</div>}
                                            {p.status === "upcoming" && <div className="as-period-status as-period-status--next">NEXT</div>}
                                            <div className="as-period-room">
                                                <i className="fas fa-door-open" /> {p.room}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>

                {/* RIGHT: Calendar + Upcoming */}
                <aside className="as-side">
                    <div className="as-calendar">
                        <div className="as-cal-head">
                            <span className="as-cal-month">{monthLabel}</span>
                            <span className="as-cal-nav">
                                <button type="button"><i className="fas fa-chevron-left" /></button>
                                <button type="button" className="as-cal-today">●</button>
                                <button type="button"><i className="fas fa-chevron-right" /></button>
                            </span>
                        </div>
                        <div className="as-cal-grid">
                            {WEEK_LABELS.map((d, i) => (
                                <div key={`wl-${i}`} className="as-cal-cell as-cal-cell--label">{d}</div>
                            ))}
                            {monthMatrix.flat().map((c, idx) => {
                                const cls = [
                                    "as-cal-cell",
                                    c.inMonth ? "" : "is-out",
                                    c.isToday ? "is-today" : "",
                                    c.holiday ? "is-holiday" : "",
                                    c.hasExam ? "is-exam" : "",
                                    c.hasEvent ? "is-event" : "",
                                ].join(" ");
                                return (
                                    <div key={idx} className={cls}>
                                        <div className="as-cal-num">{c.day}</div>
                                        {c.inMonth && c.periods > 0 && (
                                            <div className="as-cal-periods">{c.periods}p</div>
                                        )}
                                        {c.inMonth && (c.hasExam || c.hasEvent || c.holiday) && (
                                            <div className="as-cal-dot" />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="as-upcoming">
                        <div className="as-upcoming-head">
                            <span>Upcoming</span>
                            <small>Next 30 days</small>
                        </div>
                        <ul className="as-upcoming-list">
                            {upcoming.map((u, i) => (
                                <li key={i} className="as-upcoming-item">
                                    <div className="as-upcoming-date">
                                        <div className="as-upcoming-day">{u.day}</div>
                                        <div className="as-upcoming-mon">{u.month}</div>
                                    </div>
                                    <div className="as-upcoming-body">
                                        <div className="as-upcoming-title">{u.title}</div>
                                        <div className="as-upcoming-sub">{u.subtitle}</div>
                                    </div>
                                    <div className="as-upcoming-in">{u.inDays}</div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </aside>
            </div>
        </div>
    );
}
